import type {} from '@backstage/core-components';
import type {} from '@backstage/plugin-catalog';
import type {} from '@backstage/plugin-catalog-react';
import type { UnifiedThemeOptions } from '@backstage/theme';
import bannerBackground from '../images/banner-background.svg';
import { brand, fonts, radiusMd, ThemeMode } from './tokens';

declare module '@backstage/theme' {
  interface OverrideComponentNameToClassKeys {
    CatalogReactEntityProcessingStatusPickerPicker: 'input';
    PluginCatalogGraphCatalogGraphPage: 'legend';
    PluginOrgComponentsGrid: 'entityTypeBox';
    PrivateTabIndicator: 'colorPrimary';
  }
}

type Components = NonNullable<UnifiedThemeOptions['components']>;

const important = (value: string) => `${value} !important`;

const inheritColor = { color: important('inherit'), fill: 'currentColor' };

const labelText = {
  fontSize: important('14px'),
  fontFamily: important(fonts.bodyBold),
};

const sidebarText = {
  fontFamily: important(fonts.body),
  fontSize: important('14px'),
  fontWeight: important('500'),
  lineHeight: important('16px'),
};

const rounded = { borderRadius: important(radiusMd) };

const pickerInput = {
  styleOverrides: { input: { ...rounded, '& > div': rounded } },
};

const displayFont = { fontFamily: important(fonts.display) };

/**
 * Backstage and plugin components are styled by their makeStyles sheet name,
 * which is the only handle that survives a production build. The overrides
 * are built per mode from literal values, because MUI v4 evaluates theme
 * overrides once, when the theme is created.
 */
export function componentOverrides(mode: ThemeMode): Components {
  const t = brand[mode];

  const banner = {
    position: 'relative',
    overflow: important('hidden'),
    backgroundColor: important(t.appBg),
    backgroundImage: important('none'),
    minHeight: important('128px'),
    '&::before': {
      content: important("''"),
      position: 'absolute',
      inset: important('0'),
      backgroundImage: important(`url("${bannerBackground}")`),
      backgroundSize: important('cover'),
      backgroundPosition: important('center'),
      opacity: important('0.3'),
      pointerEvents: 'none',
    },
  } as const;

  const bannerText = (fontFamily: string) => ({
    fontFamily: important(fontFamily),
    color: important(t.bannerFg),
  });

  const sidebarColor = { color: important(t.sidebarFg) };

  const sidebarHover = {
    '&&:hover': {
      background: important('transparent'),
      color: important(t.sidebarFgSelected),
    },
    '&&:hover svg': {
      color: important(t.sidebarFgSelected),
      fill: 'currentColor',
    },
  };

  return {
    BackstageHeader: {
      styleOverrides: {
        header: { ...banner, color: important('#fff') },
        title: bannerText(fonts.display),
        subtitle: bannerText(fonts.display),
        type: bannerText(fonts.body),
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: bannerText(fonts.bodyBold),
        value: bannerText(fonts.body),
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          ...banner,
          color: important(t.bannerFg),
          '& a[class*="colorPrimary"]': { color: important(t.bannerFg) },
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: { '& label, & [class*="MuiTypography-button"]': labelText },
      },
    },
    BackstageSelect: {
      styleOverrides: { formLabel: labelText },
    } as Components['BackstageSelect'],
    BackstageSelectInputBase: {
      styleOverrides: {
        input: { ...rounded, '& > div[role="button"]': rounded },
      },
    },
    BackstageClosedDropdown: {
      styleOverrides: {
        icon: { color: important(t.accent), fill: 'currentColor' },
      },
    },
    CatalogReactEntityOwnerPicker: pickerInput,
    CatalogReactEntityLifecyclePicker: pickerInput,
    CatalogReactEntityTagPicker: pickerInput,
    CatalogReactEntityProcessingStatusPickerPicker: pickerInput,
    CatalogReactUserListPicker: {
      styleOverrides: {
        title: labelText,
        root: { boxShadow: important('none') },
        groupWrapper: { boxShadow: important('none') },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          paddingBottom: important('5px'),
          paddingLeft: important('22px'),
        },
        headerTitle: { fontWeight: important('400') },
        headerSubheader: {
          ...displayFont,
          '&:empty': {
            display: important('none'),
            visibility: important('hidden'),
          },
        },
      },
    },
    BackstageIconLinkVertical: { styleOverrides: { label: displayFont } },
    BackstageMarkdownContent: { styleOverrides: { markdown: displayFont } },
    BackstageHeaderIconLinkRow: {
      styleOverrides: {
        links: {
          '&:has(.tt-ownership-status)': { display: 'flex', gap: '24px' },
          '& > *:has(.tt-ownership-status)': {
            order: '1',
            marginLeft: 'auto',
          },
        },
      },
    },
    BackstageBottomLink: {
      styleOverrides: {
        boxTitle: {
          '& p': {
            color: important(t.accent),
            fontFamily: important(fonts.display),
            fontSize: important('12px'),
          },
          '& p::after': { content: "' \\2192'" },
        },
        arrow: { display: important('none') },
      },
    },
    BackstageLoginRequestListItem: {
      styleOverrides: {
        root: { '& > div:nth-child(2)': { flex: '1 1 250px' } },
      },
    },
    PluginOrgComponentsGrid: {
      styleOverrides: {
        entityTypeBox: {
          backgroundColor: important(t.accent),
          backgroundImage: important('none'),
          color: important('#fff'),
        },
      },
    },
    PluginCatalogGraphCatalogGraphPage: {
      styleOverrides: {
        legend: {
          top: important('0'),
          bottom: important('100vh'),
          right: important('5px'),
          color: important(t.accent),
        },
      },
    },
    PluginCatalogEntityContextMenu: {
      styleOverrides: {
        button: { color: important(t.fgPrimary), '& svg': inheritColor },
      },
    },
    PrivateTabIndicator: {
      styleOverrides: {
        colorPrimary: { backgroundColor: important(t.accent) },
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          '&&': sidebarColor,
          '&& hr': {
            marginTop: important('2px'),
            marginBottom: important('2px'),
            borderColor: important(t.sidebarDivider),
            backgroundColor: important(t.sidebarDivider),
          },
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          ...sidebarText,
          transition: 'opacity 0.15s ease',
          '&&': sidebarColor,
          '&& svg': inheritColor,
          ...sidebarHover,
        },
        buttonItem: { ...sidebarText, transition: 'opacity 0.15s ease' },
        label: sidebarText,
        expandButton: { '&&': sidebarColor, '&& svg': inheritColor },
        selected: {
          '&&': {
            background: important(t.accent),
            color: important(t.appBg),
          },
          '&& svg': { color: important(t.appBg), fill: 'currentColor' },
        },
      },
    },
    BackstageSidebarSubmenu: {
      styleOverrides: { title: { '&&': sidebarColor } },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: { '&&': sidebarColor, '&& svg': inheritColor, ...sidebarHover },
        dropdownItem: { '&&': sidebarColor, ...sidebarHover },
        textContent: { '&&': sidebarColor },
        selected: {
          '&&': {
            background: important(t.accent),
            color: important(t.sidebarFgSelected),
          },
          '&& svg': {
            color: important(t.sidebarFgSelected),
            fill: 'currentColor',
          },
        },
      },
    },
  };
}
